import React, { useEffect, useId, useState } from "react";
import { useSelector } from "react-redux";
import Money from "components/money";
import Collapsible from "react-collapsible";
import { ReactComponent as ArrowDown } from "assets/svgs/arrow.svg";
import { useGlobalContext } from "context/global";
import clsx from "clsx/lite";
import useCartSummary from "hooks/useCartSummary";

export const Triggers = ({
	totalPrice, totalCompareAtPrice, isOpen, arrowUp = true,
	showDescription = true, titleClass = "", priceClass = ""
}) => {
	const triggerId = useId("trigger");
	const arrowId = useId("arrow");
	const { translation } = useGlobalContext();

	const TriggerContent = () => (
		<div className="mb-3 flex flex-col w-full pr-4">
			<div className="flex justify-between items-center">
				<p className={clsx("font-boldParagraph text-sm", titleClass)}>{translation.cart?.first_order_total}</p>
				<div className="flex gap-x-2 flex-row">
					<span className={clsx("font-boldParagraph text-sm", priceClass)}><Money value={totalPrice} /></span>
					{totalCompareAtPrice > 0 && totalCompareAtPrice !== totalPrice &&
						<span className="line-through !text-sm" aria-label={`Original price: ${totalCompareAtPrice}`}>
							<Money value={totalCompareAtPrice} />
						</span>}
				</div>
			</div>
			{showDescription && <>
				<span className="text-smaller opacity-40"> {translation.other?.incVatShipping}</span>
				<span className="text-smaller opacity-40"> {translation.cart?.recurringNextStep}</span>
			</>
			}
		</div>
	);

	return [
		<TriggerContent key={triggerId} />,
		<ArrowDown key={arrowId} className={clsx(
			"uppercase block float-right transition-all duration-600 mt-2 self-start",
			isOpen === !arrowUp && "rotate-180",
		)} />
	];
};

const CartSummary = ({ isSticky }) => {
	const {
		translation, cartSettings: { shippingCost, min_products_required, subscription }, isOneTimeCollection
	} = useGlobalContext();
	const { subscriptionId } = useSelector((state) => state.account.subscription);
	const { activeBox, totalProgress, allGiftCards, totalQuantity } = useSelector(state => state.cart);
	const [isOpen, setOpen] = useState(false);

	const { totalBowlsPrice, totalBoxPrice, totalPrice, showFreeShipping, firstOrderDiscount } = useCartSummary();

	let disabledCheckout =
		!activeBox || totalProgress < min_products_required ||
		activeBox?.size?.value > totalProgress || subscriptionId;

	if (totalQuantity && allGiftCards)
		disabledCheckout = false;

	useEffect(() => {
		if (!disabledCheckout && !isOpen) {
			setOpen(true);
		}
	}, [disabledCheckout]);

	return (
		<>
			{/* Discounted price */}
			<div className={clsx("summaryCollapsible", isSticky && "!pt-0 !pb-2")}>
				<Collapsible
					transitionTime="200"
					open={!isSticky && isOpen}
					onOpening={() => setOpen(true)}
					onClosing={() => setOpen(false)}
					className={clsx("border-t pt-4", isSticky && "!px-0")}
					openedClassName={clsx("border-none", isSticky && "!p-0")}
					trigger={Triggers({ totalPrice, isOpen, arrowUp: false })}
				>
					{/* Total price */}
					{totalBoxPrice > 0 &&
						<div className="flex border-y py-4 justify-between items-center">
							<span className="text-sm">{translation.cart?.box_price}</span>
							<div className="text-sm">
								{(totalBowlsPrice - totalBoxPrice) > 0 &&
									<span className="line-through mr-2 text-sm" aria-label={`Original price: ${totalBowlsPrice}`}>
										<Money value={totalBowlsPrice} />
									</span>}
								<Money value={totalBoxPrice} />
							</div>
						</div>}
					{!isOneTimeCollection && firstOrderDiscount > 0 && !allGiftCards && (
						<div className="flex justify-between items-center py-4">
							<span className="text-sm text-orange">
								{translation.cart?.first_order_discount} (-{subscription.discountPercentage}%)
							</span>
							<span className="text-orange text-sm"> -<Money value={firstOrderDiscount} /> </span>
						</div>
					)}
					{!allGiftCards && (
						<div className={clsx("flex justify-between items-center pb-4 md:pt-0", isOneTimeCollection && "pt-4")}>
							<span className="text-sm capitalize">{translation?.subscription?.shipping}</span>
							<span className="text-sm">{showFreeShipping ? "Free" : <Money value={shippingCost} />}</span>
						</div>
					)}
				</Collapsible>
			</div>
		</>
	);
};

export default CartSummary;